import UnauthenticatedError from '../../api/helpers/UnauthenticatedError';
import {useSecurityStore} from '../../stores/securityStore';
import {useInformationStore} from '../../stores/informationStore';
import {allRequestsSettled} from '../../shared/composables/useDeviceStorage/capacitorStorage';
import type {NavigationGuardReturn, RouteLocationNormalized} from 'vue-router';
import {handleTokenRefresh} from '@/api/authentication/handleTokenRefresh';
import {reportMessage} from '@deskly/observability';

/**
 * Runs initial refresh token / information call logic
 * @return {NavigationGuardResult}
 */
export async function refreshInformationData(
    to: RouteLocationNormalized
): Promise<NavigationGuardReturn> {
    const securityStore = useSecurityStore();
    const informationStore = useInformationStore();

    if (informationStore.isInitialized) {
        return;
    }

    // Special handling for routes that do not differentiate between
    // authenticated and unauthenticated users
    //
    // Currently only used of kiosk mode pages
    if (to.meta.skipTokenRefresh) {
        return;
    }

    await waitForStorage();

    try {
        // manually call token refresh to prevent automatic token refresh during potential page reloads
        await handleTokenRefresh();
        await securityStore.refreshAccessToken();
    } catch (err) {
        if (!(err instanceof UnauthenticatedError)) {
            throw err;
        }
    }
}
/**
 * Wait until all ongoing Keychain reads are settled (or give up after {@link timeOut}).
 * On iOS, a Keychain migration after a native-app update can stall securityd XPC
 * calls for 30–60 s. Capping the wait lets the app proceed to the login screen
 * instead of showing a blank screen for that duration.
 * @param timeOut
 */
async function waitForStorage(timeOutSeconds: number = 5) {
    let settled = false;
    await Promise.race([
        allRequestsSettled().then(() => (settled = true)),
        new Promise<void>(resolve =>
            setTimeout(() => {
                if (!settled) {
                    reportMessage('refreshInformationData: waitForStorage timed out', 'warning', {
                        timeOutSeconds,
                    });
                }
                resolve();
            }, timeOutSeconds * 1000)
        ),
    ]);
}
